import firebase from "firebase/compat/app";
import { db } from "services/firebase";

/**
 * Turns the ids in a usage rollup's breakdown maps into names a human can read.
 *
 * The rollups store ids, not names, on purpose: a counter written on every LLM call must not
 * carry a display string that goes stale the moment someone is renamed. The cost of that is
 * one lookup here, so keep it bounded — resolve only the ids actually on screen, batched ten
 * at a time (Firestore's `in` limit), never the whole users or organizations collection.
 */

const CHUNK = 10;

const chunked = (items) => {
  const out = [];
  for (let i = 0; i < items.length; i += CHUNK) out.push(items.slice(i, i + CHUNK));
  return out;
};

/**
 * Names already resolved this session, per collection.
 *
 * Cached by id rather than by request, because the requests overlap heavily: switching Group by
 * away from User and back asks for the same 25 people, and widening the period from 7 days to
 * 90 asks for those 25 plus a few more. Keyed per id, only the few new ones cost a read.
 *
 * No TTL. A rename mid-session showing the old name is not worth a read per page visit, and
 * a hard refresh clears it. `resolvedNothing` remembers ids that came back empty — a deleted
 * organization would otherwise be re-queried on every single render pass.
 */
const nameCache = new Map();
const resolvedNothing = new Map();

const cacheFor = (map, collection) => {
  if (!map.has(collection)) map.set(collection, new Map());
  return map.get(collection);
};

/** Forget every resolved name. Call on logout so the next account starts clean. */
export function clearUsageLabelCache() {
  nameCache.clear();
  resolvedNothing.clear();
}

/**
 * Fetch display labels for a set of document ids.
 *
 * Returns a plain id → label map covering every id asked for that could be resolved, whether
 * it came from cache or from Firestore. Ids that resolve to nothing are simply absent, so
 * callers fall back to showing the id rather than a blank cell.
 */
async function fetchLabels(collection, ids, pickLabel) {
  const known = cacheFor(nameCache, collection);
  const missing = cacheFor(resolvedNothing, collection);
  const wanted = [...new Set((ids || []).filter(Boolean))];

  const labels = {};
  const toFetch = [];
  wanted.forEach((id) => {
    if (known.has(id)) labels[id] = known.get(id);
    else if (!missing.has(id)) toFetch.push(id);
  });

  if (!toFetch.length) return labels;

  const results = await Promise.all(
    chunked(toFetch).map((chunk) =>
      db
        .collection(collection)
        .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
        .get()
        // One bad chunk should not blank out the whole table.
        .catch((error) => {
          console.error(`Failed to load ${collection} labels:`, error);
          return null;
        }),
    ),
  );

  const failedChunks = new Set();
  results.forEach((snap, i) => {
    if (!snap) {
      // A chunk that errored is not "resolved to nothing" — retry it next time.
      chunked(toFetch)[i].forEach((id) => failedChunks.add(id));
      return;
    }
    snap.forEach((doc) => {
      const label = pickLabel(doc.data() || {});
      if (label) {
        labels[doc.id] = label;
        known.set(doc.id, label);
      }
    });
  });

  toFetch.forEach((id) => {
    if (!labels[id] && !failedChunks.has(id)) missing.set(id, true);
  });

  return labels;
}

/** "no-org" is the sentinel query_llm writes for calls made outside any organization. */
export const NO_ORG_KEY = "no-org";

export const fetchOrganizationNames = (ids) =>
  fetchLabels("organizations", ids.filter((id) => id !== NO_ORG_KEY), (data) =>
    data.name || data.organizationName || data.displayName || "",
  );

/**
 * Users are labelled by name and email together — a global admin needs to identify a person,
 * and two clinicians can share a first name. Email alone is what most of these docs have.
 */
export const fetchUserLabels = (ids) =>
  fetchLabels("users", ids, (data) => {
    const name =
      data.fullName ||
      [data.firstName, data.lastName].filter(Boolean).join(" ") ||
      data.name ||
      data.displayName ||
      "";
    const email = data.email || "";
    if (name && email) return `${name} (${email})`;
    return name || email || "";
  });
